import { Component, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { FormControl, FormGroup, FormGroupDirective } from '@angular/forms';

import { CompanyDetailsFromIdService } from '../../services/company-details-from-id.service';
import {
  APP_USER_IDAM_ADD_BUSINESS_PROFILE_ROUTE,
  APP_USER_TAX_ID_CHECK_ROUTE,
  CountryCode,
  NUMBER_0,
  NUMBER_2,
} from '~shared/constants';
import { IBranchEntries } from '~app/modules/authorization/models/branch.model';
import { Subject, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ApplicationEventService } from '~shared/modules/application-event/services';
import { AuthorizationApplicationEvent } from '~shared/modules/application-event/models';
import {
  ApplicationAuthorizationActionType,
  KvkAuthorizationLabels,
} from '~shared/modules/application-event/constants';
import { MarketDetectionUtil } from '~shared/utils/market-detection.util';
import {
  IEntrepreneurInfoFrance,
  IEntrepreneurInfoNetherlands,
} from '~app/modules/account/modules/account-management/models/user-info.model';
import { ActiveFeaturesConfigcatService } from '~shared/modules/active-features/services';
import { FF_ENABLE_BRANCH_SELECTION_DESIGN } from '../../constants/branch-selection.contant';

@Component({
  selector: 'app-branch-selection',
  templateUrl: './branch-selection.component.html',
  styleUrls: ['./branch-selection.component.scss'],
  providers: [FormGroupDirective],
})
export class BranchSelectionComponent implements OnDestroy {
  branches: IBranchEntries[];
  isLoading = true;
  destroySubject = new Subject();
  taxNumber: string;
  branchesForm: FormGroup;
  currentCountryCode = MarketDetectionUtil.getCountryCode().toUpperCase();
  number2 = NUMBER_2;
  isNewBranchSelectionDesignEnabled$ = this.featureFlagService.isFeatureEnabledReadyForCurrentCountry$(
    FF_ENABLE_BRANCH_SELECTION_DESIGN
  );
  isFrenchMarket = this.currentCountryCode === CountryCode.France.toUpperCase();

  constructor(
    private router: Router,
    private applicationEventService: ApplicationEventService,
    private companyDetailsFromIdService: CompanyDetailsFromIdService,
    private featureFlagService: ActiveFeaturesConfigcatService
  ) {
    this.branchesForm = new FormGroup({
      selectedBranch: new FormControl('', (control) => (!control.value ? { selectedBranch: 'empty' } : {})),
    });
    const { queryParams } = this.router.parseUrl(this.router.url);
    const { backUrl, taxNumber } = queryParams;

    this.companyDetailsFromIdService.branchData$.pipe(takeUntil(this.destroySubject)).subscribe((entries) => {
      if (!entries || entries.length === NUMBER_0) {
        this.router.navigate([APP_USER_TAX_ID_CHECK_ROUTE], {
          queryParams: { backUrl },
        });
      }

      this.isLoading = false;
      this.branches = entries;
      this.taxNumber = taxNumber;
    });
  }

  replaceBranchNumberUnderscore = (branchNumber) =>
    branchNumber && branchNumber.includes('_') ? branchNumber.replace('_', '') : branchNumber;

  replacePinCode = (address, pinCode) =>
    address && pinCode ? address.replace(new RegExp(pinCode + ',?\\s*', 'g'), '') : address;

  goToTierTwoSignup() {
    this.companyDetailsFromIdService
      .getSelectedBranchData(this.taxNumber, this.branchesForm.get('selectedBranch').value)
      .pipe(takeUntil(this.destroySubject))
      .subscribe(
        (selectedBranchData) => {
          const tier2Data = this.companyDetailsFromIdService.formatTierTwoDataForSignup(
            selectedBranchData,
            this.taxNumber,
            false
          );
          this.companyDetailsFromIdService.postCompanyData(
            tier2Data as IEntrepreneurInfoNetherlands | IEntrepreneurInfoFrance
          );
          this.companyDetailsFromIdService.publishSelectedBranchDataForTierTwo(selectedBranchData);

          const { queryParams } = this.router.parseUrl(this.router.url);
          const { backUrl } = queryParams;

          this.router
            .navigate([APP_USER_IDAM_ADD_BUSINESS_PROFILE_ROUTE], {
              queryParams: { backUrl },
            })
            .then(() => {
              this.applicationEventService.publish(
                new AuthorizationApplicationEvent(
                  ApplicationAuthorizationActionType.SelectKvkBranch,
                  selectedBranchData?.branchNumber ?? selectedBranchData?.rsin
                ),
                true
              );
            });
        },
        () => {
          this.applicationEventService.publish(
            new AuthorizationApplicationEvent(
              ApplicationAuthorizationActionType.SignupError,
              KvkAuthorizationLabels.FailedBusiness
            ),
            true
          );
        }
      );
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
  }
}
